/**
 * D-020 Live Trading V2 — the normalized one-click/hotkey order intent and its
 * SUBMIT-TIME guard.
 *
 * V1's stale-line / taker guard lived entirely in render-time pill disables:
 * `oneClickStore.submit` itself checked only price range + pacing, so nothing
 * revalidated between render and click. Every LT2 input path (mouse pill,
 * ladder level, A/D hotkey, any surface) now builds one `LiveOrderIntent` and
 * passes through `evaluateIntentGuard` against the CURRENT book at submit
 * time. The guard is pure and synchronous — books re-derive per market frame,
 * so at click time the book handed in is at most one frame old.
 *
 * Guard rules, in refusal-priority order:
 *   1. identity  — the book's ticker must equal the intent's ticker. This is
 *                  the LT2-006 "stable market identity" enforcement: a
 *                  re-sorted DOM or swapped card can never fire at the wrong
 *                  market.
 *   2. availability — classifyLineAvailability must say 'active'
 *                  (absent / mapping-error / suspended / stale all refuse,
 *                  each with its own reason).
 *   3. price     — integer 1..99 (same rule V1 enforced).
 *   4. role      — a postOnly intent whose price would CROSS (taker) is
 *                  refused locally with a truthful message. The venue's
 *                  post-only-cross rejection remains the backstop; role
 *                  `null` (unknown opposing depth) passes — unknown is not
 *                  proof of taker (see limitOrderRole).
 */
import type { VenueBook } from '@/views/ValueDashboard/types';
import { limitOrderRole } from '@/views/ValueDashboard/tradeModalBook';
import {
  classifyLineAvailability,
  LINE_AVAILABILITY_LABEL,
  type LineAvailability,
} from '@/lib/sport/lineAvailability';
import type { Lt2ClockStamp } from './lt2Timing';

export type Lt2InputMethod = 'mouse' | 'hotkey-a' | 'hotkey-d' | 'hotkey-w' | 'hotkey-e';

export type Lt2Surface =
  | 'dash-moneyline'
  | 'dash-multi-outcome'
  | 'dash-spread-total'
  | 'gd-moneyline'
  | 'gd-spread-total'
  | 'gd-game-lines';

export type Lt2Period = 'full' | '1H';

/** Coverage boundary (release record, D-020): only full-game and 1st-half
 *  derivative markets are one-click eligible. Null = outside the boundary —
 *  callers fall through to the ticket. */
export function lt2PeriodFor(period: string): Lt2Period | null {
  return period === 'full' ? 'full' : period === '1H' ? '1H' : null;
}

export interface LiveOrderIntent {
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  postOnly: boolean;
  /** Short human label for the ack line, e.g. "DAL -3.5 NO @ 77¢". */
  label: string;
  inputMethod: Lt2InputMethod;
  surface: Lt2Surface;
  marketKind: 'moneyline' | 'multi-outcome' | 'spread' | 'total';
  period: Lt2Period;
  /** Spread/total line value; null/undefined for moneyline. */
  line?: number | null;
  /** The hotkey focus card that produced this intent, if any. */
  focusedCardId?: string | null;
  /** Date.now() when the displayed price this intent copies was rendered. */
  displayedPriceMs: number;
  /** Date.now() at the DOM input event (keydown/click) that produced this
   *  intent — the anchor for acknowledgement-parity timing. */
  inputAtMs?: number;
  /** Same-page dual-clock stamp for local acceptance telemetry only. The
   *  audit builder and order payload must not copy it. */
  inputAtClock?: Lt2ClockStamp;
  /** Live Trading V3 Attack provenance — present ONLY on hotkey-w/e intents.
   *  Rides into the `__attack` relay marker (gate input) and the audit stamp.
   *  reference = the best same-side displayed bid the target was computed
   *  from; depth = levels above it (fixed at 1 in this release). */
  attack?: {
    depth: number;
    referencePriceCents: number;
  };
}

/** Derivative intents belong to the separately entitled LT2 lane on EVERY
 *  input path. Keep this allowlist explicit so a future intent kind cannot
 *  acquire the LT2 audit/capability contract merely by sharing the guarded
 *  submit path — the exclusion is per input path, not per feature: see
 *  `lt2AuditRequired` for the hotkey rule (#647). */
export const LT2_AUDIT_MARKET_KINDS = new Set<LiveOrderIntent['marketKind']>([
  'spread',
  'total',
]);

/** Whether this intent must carry the `__lt2` audit/capability stamp.
 *
 *  Two ways in, deliberately (#647): every intent of an LT2 market kind, on
 *  any input path — and every HOTKEY intent, on any market kind. A/D orders
 *  are the LT2 hotkey capability itself, so they carry the relay-enforced
 *  entitlement, explicit size ceiling, and receipt input tag even where the
 *  market kind (moneyline) stays outside the kind allowlist. Mouse moneyline
 *  clicks remain unmarked — widening the KIND set would change ordinary click
 *  trading's relay regime, which #647 forbids. */
export function lt2AuditRequired(
  intent: Pick<LiveOrderIntent, 'marketKind' | 'inputMethod'>,
): boolean {
  return LT2_AUDIT_MARKET_KINDS.has(intent.marketKind) || intent.inputMethod !== 'mouse';
}

export type IntentGuardOutcome =
  | { ok: true; role: 'maker' | 'taker' | null; availability: 'active' }
  | {
      ok: false;
      code: Exclude<LineAvailability, 'active'> | 'taker-on-postonly' | 'price-range';
      message: string;
    };

export function evaluateIntentGuard(
  intent: LiveOrderIntent,
  book: VenueBook | null | undefined,
): IntentGuardOutcome {
  // Identity first: a book for a different ticker means the click resolved
  // through a mismatched card/row — fail closed as a mapping error.
  if (book != null && book.marketTicker !== intent.ticker) {
    return {
      ok: false,
      code: 'mapping-error',
      message: 'Market identity mismatch — click did not resolve to the displayed market',
    };
  }
  const availability = classifyLineAvailability(book);
  if (availability !== 'active') {
    const reason = book?.sourceValidity === 'unconfirmed' && availability === 'stale'
      ? 'Source confirmation unavailable' : LINE_AVAILABILITY_LABEL[availability];
    return {
      ok: false,
      code: availability,
      message:
        book?.sourceValidity === 'unconfirmed'
          ? `${reason} — order not sent`
        : availability === 'stale'
          ? 'Market is stale — trading is unavailable until the feed resumes'
          : `${reason} — order not sent`,
    };
  }
  if (
    !Number.isInteger(intent.priceCents) ||
    intent.priceCents < 1 ||
    intent.priceCents > 99
  ) {
    return { ok: false, code: 'price-range', message: 'Price out of range' };
  }
  const role = limitOrderRole(book ?? undefined, intent.side, intent.priceCents);
  if (intent.postOnly && role === 'taker') {
    return {
      ok: false,
      code: 'taker-on-postonly',
      message: 'Price would cross the book — maker order not sent',
    };
  }
  return { ok: true, role, availability: 'active' };
}

/** The `__lt2` audit stamp stored verbatim in trades.request_payload (the
 *  relay's venue-body allowlist keeps it off the wire). Everything the
 *  packet's telemetry section asks of the client side, in one place. */
export function buildLt2AuditMeta(
  intent: LiveOrderIntent,
  guard: Extract<IntentGuardOutcome, { ok: true }>,
  book: VenueBook | null | undefined,
): Record<string, unknown> {
  return {
    v: 1,
    input: intent.inputMethod,
    surface: intent.surface,
    marketKind: intent.marketKind,
    period: intent.period,
    ...(intent.line != null ? { line: intent.line } : {}),
    ...(intent.focusedCardId != null ? { focusedCardId: intent.focusedCardId } : {}),
    displayedPriceMs: intent.displayedPriceMs,
    ...(intent.inputAtMs != null ? { inputAtMs: intent.inputAtMs } : {}),
    clickMs: Date.now(),
    ...(intent.attack != null ? { attack: { ...intent.attack } } : {}),
    guard: {
      availability: guard.availability,
      role: guard.role,
      freshness: book?.freshness ?? null,
    },
  };
}
